import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Trash2, Pencil, Check, X } from "lucide-react";

const fmtStamp = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch { return iso; }
};

export default function NotesPanel({ appId }) {
  const { user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState("");
  const [posting, setPosting] = useState(false);
  const [editing, setEditing] = useState(null); // {id, body}

  const canUse = user && ["admin", "loan_rep", "broker"].includes(user.role);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/applications/${appId}/notes`);
      setNotes(r.data || []);
    } catch (e) {
      // silent — user might not have visibility on this file
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!canUse || !appId) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId, canUse]);

  const post = async () => {
    const b = body.trim();
    if (!b) { toast.error("Note is empty"); return; }
    setPosting(true);
    try {
      const r = await api.post(`/applications/${appId}/notes`, { body: b });
      setNotes((prev) => [r.data, ...prev]);
      setBody("");
      toast.success("Note posted");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPosting(false);
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    const b = (editing.body || "").trim();
    if (!b) { toast.error("Note is empty"); return; }
    try {
      const r = await api.patch(`/applications/${appId}/notes/${editing.id}`, { body: b });
      setNotes((prev) => prev.map((x) => (x.id === editing.id ? r.data : x)));
      setEditing(null);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const removeNote = async (n) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      await api.delete(`/applications/${appId}/notes/${n.id}`);
      setNotes((prev) => prev.filter((x) => x.id !== n.id));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!canUse) return null;

  return (
    <section className="py-8" data-testid="notes-panel">
      <div className="flex items-baseline justify-between">
        <div>
          <div className="small-caps text-slate-500">Notes</div>
          <h3 className="mt-1 font-semibold text-[22px] text-[#1F2937]">Public Deal Notes</h3>
          <p className="mt-1 text-[12px] text-slate-500">
            Visible to the file&apos;s loan officer, its broker, and admin. Notes on
            duplicated files stay separate — other loan officers can&apos;t see them.
          </p>
        </div>
        <div className="small-caps text-slate-400 text-[10px]">
          {notes.length} {notes.length === 1 ? "note" : "notes"}
        </div>
      </div>

      <div className="mt-6 border border-slate-200 bg-white p-4">
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={3}
          placeholder="Log a note about this file — call summary, next step, red flag…"
          data-testid="notes-input"
          className="border-0 shadow-none p-0 focus-visible:ring-0 focus-visible:ring-offset-0 resize-none text-[14.5px]"
        />
        <div className="mt-3 flex items-center justify-end border-t border-slate-100 pt-3">
          <button
            onClick={post}
            disabled={posting || !body.trim()}
            data-testid="notes-post-btn"
            className="inline-flex items-center gap-2 bg-[#1F2937] text-white px-4 py-2 small-caps disabled:opacity-40"
          >
            {posting ? "Posting…" : "Post note"}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="mt-6 small-caps text-slate-400">Loading…</div>
      ) : notes.length === 0 ? (
        <div className="mt-6 small-caps text-slate-400">No notes yet.</div>
      ) : (
        <ul className="mt-6 space-y-3" data-testid="notes-list">
          {notes.map((n) => {
            const mine = n.author_id === user.id;
            const canEdit = mine || user.role === "admin";
            const isEditing = editing?.id === n.id;
            return (
              <li key={n.id} className="border border-slate-200 bg-white p-4" data-testid={`note-${n.id}`}>
                <div className="flex items-baseline justify-between gap-3 flex-wrap">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="font-semibold text-[14px] text-[#1F2937]">{n.author_name}</span>
                    <span className="small-caps text-slate-400 text-[10px]">
                      {n.author_role === "loan_rep" ? "Loan Officer" : n.author_role}
                    </span>
                    <span className="small-caps text-slate-400 text-[10px]">· {fmtStamp(n.created_at)}</span>
                    {n.updated_at && n.updated_at !== n.created_at && (
                      <span className="small-caps text-slate-400 text-[10px] italic">edited</span>
                    )}
                  </div>
                  {canEdit && !isEditing && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setEditing({ id: n.id, body: n.body })}
                        className="p-1 text-slate-500 hover:text-[#1F2937]"
                        data-testid={`note-edit-${n.id}`}
                        title="Edit note"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => removeNote(n)}
                        className="p-1 text-slate-500 hover:text-red-700"
                        data-testid={`note-delete-${n.id}`}
                        title="Delete note"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                </div>
                {isEditing ? (
                  <div className="mt-3 space-y-2">
                    <Textarea
                      value={editing.body}
                      onChange={(e) => setEditing((prev) => ({ ...prev, body: e.target.value }))}
                      rows={3}
                      data-testid={`note-edit-input-${n.id}`}
                      className="border-slate-300"
                    />
                    <div className="flex items-center justify-end gap-3">
                      <button onClick={() => setEditing(null)} className="inline-flex items-center gap-1 small-caps text-slate-500 hover:text-[#1F2937]">
                        <X className="h-3.5 w-3.5" /> Cancel
                      </button>
                      <button onClick={saveEdit} className="inline-flex items-center gap-1 bg-[#1F2937] text-white px-3 py-1.5 small-caps">
                        <Check className="h-3.5 w-3.5" /> Save
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="mt-2 text-[14.5px] leading-[1.55] text-[#1F2937] whitespace-pre-wrap">{n.body}</p>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
